import { Award, FileText, MapPin, Phone, Mail, Printer } from "lucide-react";
import { fileUrl, money } from "@/lib/api";
import ElmMark from "@/components/ElmMark";

const fmtDate = (iso) => (iso ? new Date((iso.length <= 10 ? iso + "T12:00:00" : iso)).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : "");

function Section({ title, children, testid }) {
  return (
    <section className="mt-12 break-inside-avoid" data-testid={testid}>
      <h2 className="font-serif text-2xl text-stone-900 sm:text-3xl">{title}</h2>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function FinishChip({ f }) {
  if (f.kind === "palette") {
    return (
      <div className="overflow-hidden rounded-xl border border-stone-200 bg-white">
        <div className="flex h-14">{(f.colors || []).map((c, i) => <div key={i} className="flex-1" style={{ background: c.hex }} />)}</div>
        <div className="px-3 py-2"><p className="text-sm font-semibold text-stone-800">{f.name}</p><p className="truncate text-xs text-stone-500">{(f.colors || []).map((c) => c.name).filter(Boolean).join(" · ")}</p>{f.room && <p className="mt-0.5 text-[11px] uppercase tracking-wider text-stone-400">{f.room}</p>}</div>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-3">
      <div className="h-11 w-11 shrink-0 rounded-md border border-stone-200" style={{ background: f.hex || "#e7e5e4" }} />
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-stone-800">{f.name}</p>
        <p className="truncate text-xs text-stone-500">{[f.brand, f.code].filter(Boolean).join(" ") || [f.supplier, f.finish].filter(Boolean).join(" · ") || (f.kind === "paint" ? "Paint colour" : "Material")}</p>
        {f.room && <p className="mt-0.5 text-[11px] uppercase tracking-wider text-stone-400">{f.room}</p>}
      </div>
    </div>
  );
}

export default function HandoverView({ pack, showPrint = true }) {
  if (!pack) return null;
  const s = pack.sections || {};
  const p = pack.project || {};
  const brandName = pack.brand?.name || "E.L.M";
  const gallery = pack.gallery || [];
  const finishes = pack.finishes || [];
  const warranties = pack.warranties || [];
  const docs = pack.documents || [];
  const trades = pack.trades || [];
  const contract = pack.contract;

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900" data-testid="handover-view">
      {showPrint && (
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-stone-200 bg-white/90 px-5 py-3 backdrop-blur print:hidden sm:px-8">
          <ElmMark />
          <button onClick={() => window.print()} className="inline-flex items-center gap-1.5 rounded-full bg-stone-900 px-4 py-2 text-sm font-medium text-white hover:bg-stone-700" data-testid="handover-print"><Printer className="h-4 w-4" />Print / Save PDF</button>
        </div>
      )}
      <article className="mx-auto max-w-3xl px-5 pb-20 sm:px-8">
        <header className="relative mt-8 overflow-hidden rounded-3xl bg-stone-900 text-white">
          {pack.hero?.url && <img src={fileUrl(pack.hero.url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-45" />}
          <div className="relative px-7 py-16 sm:px-12 sm:py-24">
            <p className="text-[11px] uppercase tracking-[0.35em] text-white/60">{brandName}</p>
            <h1 className="mt-3 font-serif text-4xl leading-tight sm:text-5xl" data-testid="handover-title">{pack.title}</h1>
            {p.client_name && <p className="mt-4 text-lg text-white/80">Prepared for {p.client_name}</p>}
            <p className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/60">
              {p.name && <span>{p.name}</span>}
              {p.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{p.address}</span>}
              {p.completion_date && <span>Completed {fmtDate(p.completion_date)}</span>}
            </p>
          </div>
        </header>

        {s.thankyou && (pack.intro || pack.team_photo) && (
          <section className="mt-10 rounded-2xl border border-stone-200 bg-white p-7" data-testid="handover-intro">
            {pack.team_photo?.url && <img src={fileUrl(pack.team_photo.url)} alt="The team" className="mb-5 h-56 w-full rounded-xl object-cover" data-testid="handover-team-photo" />}
            {pack.intro && <p className="whitespace-pre-line text-base leading-relaxed text-stone-700">{pack.intro}</p>}
            <p className="mt-4 text-sm font-medium text-stone-900">— The {brandName} team</p>
          </section>
        )}

        {s.gallery && gallery.length > 0 && (
          <Section title="Your project" testid="handover-gallery">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {gallery.map((g) => (
                <figure key={g.file_id} className="overflow-hidden rounded-xl bg-stone-200">
                  <img src={fileUrl(g.url)} alt={g.caption || ""} className="aspect-square w-full object-cover" loading="lazy" />
                  {g.caption && <figcaption className="px-2 py-1 text-[11px] text-stone-500">{g.caption}</figcaption>}
                </figure>
              ))}
            </div>
          </Section>
        )}

        {s.finishes && finishes.length > 0 && (
          <Section title="What's in your home" testid="handover-finishes">
            <p className="-mt-3 mb-4 text-sm text-stone-500">Paint colours, palettes and materials specified for your project — handy for touch-ups and matching later.</p>
            <div className="grid gap-3 sm:grid-cols-2">{finishes.map((f, i) => <FinishChip key={i} f={f} />)}</div>
          </Section>
        )}

        {s.warranties && warranties.length > 0 && (
          <Section title="Warranties & guarantees" testid="handover-warranties">
            <div className="divide-y divide-stone-200 overflow-hidden rounded-2xl border border-stone-200 bg-white">
              {warranties.map((w) => (
                <div key={w.id} className="flex flex-wrap items-start justify-between gap-3 p-4" data-testid="handover-warranty-row">
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm font-semibold text-stone-800"><Award className="h-4 w-4 text-stone-400" />{w.item}</p>
                    <p className="mt-0.5 text-xs text-stone-500">{[w.provider, w.category].filter(Boolean).join(" · ")}</p>
                    {w.notes && <p className="mt-1 text-sm text-stone-600">{w.notes}</p>}
                    {(w.files || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {w.files.map((f) => <a key={f.file_id} href={fileUrl(f.url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-stone-100 px-2.5 py-1 text-xs text-stone-700 hover:bg-stone-200"><FileText className="h-3 w-3" />Document</a>)}
                      </div>
                    )}
                  </div>
                  <div className="text-right text-xs text-stone-500">
                    {w.length && <p className="text-sm font-medium text-stone-800">{w.length}</p>}
                    {w.start_date && <p>from {fmtDate(w.start_date)}</p>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {s.documents && docs.length > 0 && (
          <Section title="Documents & plans" testid="handover-documents">
            <div className="grid gap-2 sm:grid-cols-2">
              {docs.map((d, i) => (
                <a key={i} href={fileUrl(d.url)} target="_blank" rel="noreferrer" className="flex items-center gap-3 rounded-xl border border-stone-200 bg-white p-3 hover:border-stone-300" data-testid="handover-document-row">
                  <FileText className="h-5 w-5 shrink-0 text-stone-400" />
                  <span className="min-w-0"><span className="block truncate text-sm font-medium text-stone-800">{d.title}</span>{d.revision && <span className="text-xs text-stone-500">{d.revision}</span>}</span>
                </a>
              ))}
            </div>
          </Section>
        )}

        {s.trades && trades.length > 0 && (
          <Section title="Who worked on your home" testid="handover-trades">
            <div className="grid gap-3 sm:grid-cols-2">
              {trades.map((t, i) => (
                <div key={i} className="rounded-xl border border-stone-200 bg-white p-4" data-testid="handover-trade-row">
                  <p className="text-sm font-semibold text-stone-800">{t.company || t.name}</p>
                  <p className="text-xs uppercase tracking-wider text-stone-400">{t.trade}{t.company && t.name ? ` · ${t.name}` : ""}</p>
                  <div className="mt-2 flex flex-col gap-1 text-sm text-stone-600">
                    {t.phone && <a href={`tel:${t.phone}`} className="inline-flex items-center gap-1.5 hover:text-stone-900"><Phone className="h-3.5 w-3.5" />{t.phone}</a>}
                    {t.email && <a href={`mailto:${t.email}`} className="inline-flex items-center gap-1.5 hover:text-stone-900"><Mail className="h-3.5 w-3.5" />{t.email}</a>}
                  </div>
                </div>
              ))}
            </div>
          </Section>
        )}

        {s.contract && contract && (
          <Section title="Your contract summary" testid="handover-contract">
            <div className="rounded-2xl border border-stone-200 bg-white p-5">
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between"><dt className="text-stone-500">Original contract</dt><dd className="font-medium">{money(contract.original_total)}</dd></div>
                {contract.changes !== 0 && <div className="flex justify-between"><dt className="text-stone-500">Approved changes</dt><dd className="font-medium">{money(contract.changes)}</dd></div>}
                <div className="flex justify-between border-t border-stone-200 pt-2 text-base"><dt className="font-semibold">Final total</dt><dd className="font-semibold">{money(contract.current_total)}</dd></div>
              </dl>
              {contract.tax_rate ? <p className="mt-2 text-xs text-stone-400">Includes {contract.tax_rate}% tax.</p> : null}
            </div>
          </Section>
        )}

        {s.firstweek && pack.first_week && (
          <Section title="Settling in" testid="handover-firstweek">
            <ul className="space-y-2">
              {pack.first_week.split("\n").map((t) => t.trim()).filter(Boolean).map((t, i) => (
                <li key={i} className="flex gap-3 text-sm text-stone-700"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-stone-400" />{t}</li>
              ))}
            </ul>
          </Section>
        )}

        {s.review && pack.review?.url && (
          <section className="mt-12 rounded-2xl bg-stone-900 p-7 text-center text-white sm:p-10" data-testid="handover-review">
            <p className="font-serif text-2xl">{pack.review.text || "Enjoyed working with us?"}</p>
            <p className="mt-2 text-sm text-white/70">A quick review helps other homeowners find {brandName} — and means a lot to the team.</p>
            <a href={pack.review.url} target="_blank" rel="noreferrer" className="mt-5 inline-block rounded-full bg-white px-6 py-2.5 text-sm font-medium text-stone-900 hover:bg-white/90" data-testid="handover-review-link">Leave a review</a>
          </section>
        )}

        <footer className="mt-16 border-t border-stone-200 pt-5 text-center text-xs text-stone-400">Prepared by {brandName} · with E.L.M</footer>
      </article>
    </div>
  );
}
